import { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { EventInfo } from '@/api/events/types/types';
import { LineSeparator } from '@/components/LineSeparator';
import { Box } from '@/components/lib/components/Box';
import { BetslipPick } from '@/feature/betslip-pickem/types';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { PlayerRow } from '../PlayerRow';
import { HEIGHT_DURATION, RACE_TO_PLACE_BOTTOM_SEPARATOR_HEIGHT } from './race-to-place-constants';
import { RaceToPlaceModalListProps } from './types';

const RaceToPlacePlayerRow = memo(({ pick, event }: { pick: BetslipPick; event: EventInfo | undefined }) => {
    const team = eventUtils.getTeamById(event, pick.player.team?.id);
    const player = useMemo(() => ({ ...pick.player, team, projections: pick.player.projections }), [pick.player, team]);

    return (
        <PlayerRow
            mode={'race-2-place'}
            projection={pick.projection}
            projections={[pick.projection]}
            player={player}
            event={event}
            outcome={pick.outcome}
            hideBorder={false}
            presentation={'sheet'}
        />
    );
});

export const RaceToPlaceModalList = memo(
    ({ picks, events, maxHeight, isScrollable, isKeyboardOpen, dismissKeyboard }: RaceToPlaceModalListProps) => {
        // Animate height changes
        const animatedHeight = useSharedValue(maxHeight);
        const isAnimatingShared = useSharedValue(false);

        useEffect(() => {
            isAnimatingShared.value = true;
            animatedHeight.value = withTiming(maxHeight, { duration: HEIGHT_DURATION }, finished => {
                if (finished) {
                    isAnimatingShared.value = false;
                }
            });
        }, [animatedHeight, isAnimatingShared, maxHeight]);

        const animatedStyle = useAnimatedStyle(() => ({
            maxHeight: animatedHeight.value,
            pointerEvents: isAnimatingShared.value ? ('none' as const) : ('auto' as const),
        }));

        // Scroll is disabled when keyboard is open
        const scrollEnabled = isScrollable && !isKeyboardOpen;

        const handleTouchStart = useCallback(() => {
            dismissKeyboard?.();
            // Let touch pass through to buttons/scrollView
            return false;
        }, [dismissKeyboard]);

        const renderItem = useCallback(
            ({ item }: { item: BetslipPick }) => {
                const event = events?.find(ev => ev.id === item.eventId);
                return <RaceToPlacePlayerRow pick={item} event={event} />;
            },
            [events]
        );

        return (
            <Animated.View style={animatedStyle} onStartShouldSetResponderCapture={handleTouchStart}>
                <FlatList<BetslipPick>
                    data={picks}
                    contentContainerStyle={styles.listContainer}
                    keyExtractor={item => `${createPlayerEventKey(item.player.id, item.eventId)}`}
                    ItemSeparatorComponent={LineSeparator}
                    showsVerticalScrollIndicator={showScrollIndicator}
                    renderItem={renderItem}
                    scrollEnabled={scrollEnabled}
                />
                <Box pb={'s16'} height={RACE_TO_PLACE_BOTTOM_SEPARATOR_HEIGHT}>
                    <LineSeparator />
                </Box>
            </Animated.View>
        );
    }
);

const styles = StyleSheet.create({
    listContainer: {
        paddingHorizontal: 16,
        paddingTop: 16,
    },
});
